import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { addMeta } from 'utils/reduceByKey'

interface FetchInvitePayload {
  carriers: Record<number, Carrier>
  invites: Record<number, Omit<CarrierInvite, 'meta'>>
}

interface CarrierWithMeta extends Carrier {
  meta: ObjectMeta
}

interface State {
  invites: Record<number, CarrierInvite>
  invitedCarriers: Record<number, CarrierWithMeta>
  meta: {
    isLoading: boolean
  }
}

const initialState: State = {
  invites: {},
  invitedCarriers: {},
  meta: {
    isLoading: false,
  },
}

export const slice = createSlice({
  name: 'carrierInvites',
  initialState,
  reducers: {
    createInviteFailed(
      state: State,
      action: PayloadAction<{
        invite: Omit<CarrierInvite, 'id' | 'meta'>
        error: string
      }>
    ) {
      const { carrier } = action.payload.invite
      state.invites[carrier].meta.isUpdating = false
      state.invites[carrier].meta.error = action.payload.error
      delete state.invitedCarriers[carrier]
    },
    createInviteStart(
      state: State,
      action: PayloadAction<{
        carrier: Carrier
        invite: Omit<CarrierInvite, 'id' | 'meta'>
      }>
    ) {
      const invite: CarrierInvite = {
        ...action.payload.invite,
        id: 0,
        meta: {
          error: null,
          isUpdating: true,
        },
      }
      state.invitedCarriers[invite.carrier] = {
        ...action.payload.carrier,
        invite: invite.id,
        meta: {
          error: null,
          isUpdating: false,
        },
      }
      state.invites[invite.carrier] = invite
    },
    createInviteSuccess(
      state: State,
      action: PayloadAction<{
        carrier: Carrier
        invite: CarrierInvite
      }>
    ) {
      const invite = {
        ...action.payload.invite,
        meta: {
          error: null,
          isUpdating: false,
        },
      }
      state.invitedCarriers[invite.carrier].invite = invite.id
      state.invites[invite.carrier] = invite
    },
    deleteInviteFailed(
      state: State,
      action: PayloadAction<{
        carrier: Carrier
        invite: CarrierInvite
        error: string
      }>
    ) {
      const { carrier, invite } = action.payload
      state.invites[invite.carrier].meta.isUpdating = false
      state.invites[invite.carrier].meta.error = action.payload.error
      state.invitedCarriers[invite.carrier] = {
        ...carrier,
        invite: invite.id,
        meta: {
          error: null,
          isUpdating: false,
        },
      }
    },
    deleteInviteStart(
      state: State,
      action: PayloadAction<{
        carrier: Carrier
        invite: CarrierInvite
      }>
    ) {
      state.invites[action.payload.carrier.id].meta.isUpdating = true
      state.invites[action.payload.carrier.id].meta.error = null
      delete state.invitedCarriers[action.payload.invite.carrier]
    },
    deleteInviteSuccess(state: State, action: PayloadAction<CarrierInvite>) {
      delete state.invites[action.payload.carrier]
      delete state.invitedCarriers[action.payload.carrier]
    },
    fetchInvitesFailed(state: State) {
      state.meta.isLoading = false
    },
    fetchInvitesStart(state: State) {
      state.meta.isLoading = true
    },
    fetchInvitesSuccess(
      state: State,
      action: PayloadAction<FetchInvitePayload>
    ) {
      state.invites = addMeta<CarrierInvite>(action.payload.invites)
      state.invitedCarriers = addMeta<CarrierWithMeta>(action.payload.carriers)
      state.meta.isLoading = false
    },
  },
})

export default slice.reducer
